import axios, { AxiosError, AxiosResponse } from 'axios'

const RETRY_NOW_EVENT = 'cobalt:retry-now'

// Statuses below 500 that mean "try again later" rather than give a verdict.
const RETRYABLE_STATUSES = new Set([408, 429])

/**
 * Why a request got no usable verdict from the server (offline, a network
 * change, throttling, a server error), or null when it got a real answer that
 * retrying will not change.
 */
export function unansweredReason(error: unknown): string | null {
    if (!axios.isAxiosError(error)) {
        return null
    }
    if (!error.response) {
        return 'network'
    }
    const { status } = error.response
    return status >= 500 || RETRYABLE_STATUSES.has(status)
        ? `status ${status}`
        : null
}

/**
 * The API clients resolve every status below 500, so a throttled or timed-out
 * request comes back as a response rather than an error. Throw it instead, so
 * callers that retry unanswered requests see it as one.
 */
export function throwIfRetryableResponse(response: AxiosResponse): void {
    if (RETRYABLE_STATUSES.has(response.status)) {
        throw new AxiosError(
            `Request failed with status code ${response.status}`,
            AxiosError.ERR_BAD_REQUEST,
            response.config,
            response.request,
            response
        )
    }
}

export function retryBackoffMs(
    failureCount: number,
    { retryBaseMs, retryMaxMs }: { retryBaseMs: number; retryMaxMs: number }
): number {
    return Math.min(retryBaseMs * 2 ** (failureCount - 1), retryMaxMs)
}

/** Ends every pending `waitForRetryOpportunity` at once, e.g. a "Try now" button. */
export function requestRetryNow(): void {
    window.dispatchEvent(new Event(RETRY_NOW_EVENT))
}

/**
 * Resolves after `backoffMs`, or as soon as the browser reports it is back
 * online, the tab is shown again, or `requestRetryNow` is called, whichever
 * comes first.
 */
export async function waitForRetryOpportunity(
    backoffMs: number
): Promise<void> {
    let wake = (): void => {}
    const handleWake = (): void => wake()
    const handleVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') {
            wake()
        }
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    window.addEventListener('online', handleWake)
    window.addEventListener(RETRY_NOW_EVENT, handleWake)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    try {
        await new Promise<void>((resolve) => {
            wake = resolve
            timer = setTimeout(resolve, backoffMs)
        })
    } finally {
        clearTimeout(timer)
        window.removeEventListener('online', handleWake)
        window.removeEventListener(RETRY_NOW_EVENT, handleWake)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
}
